import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { RiBillLine } from "react-icons/ri";
import { MdOutlineCalendarMonth } from "react-icons/md";
import DeleteIcon from "@mui/icons-material/Delete";
import CInput from "../../../components/CInput";
import { renderCurrencyIcon } from "../../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";

const amountCellStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "6px",
  color: "#0d0d0e",
  fontSize: "14px",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const inputColorSx = {
  "& .MuiInputBase-input": {
    color: "#0d0d0e !important",
    WebkitTextFillColor: "#0d0d0e !important",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#0d0d0e !important",
    WebkitTextFillColor: "#0d0d0e !important",
  },
};

const BillingMilestones = ({
  billingMilestonesData,
  billingMilestones,
  hasMilestoneChanges,
  isMilestoneLoading,
  addMilestone,
  deleteMilestone,
  updateMilestone,
  saveMilestoneChanges,
  calculateMilestoneAmount,
  isDisabled = false,
  hideSaveButton = false,
}) => {
  const rowRefs = useRef([]);
  const [prevCount, setPrevCount] = useState(billingMilestones?.length || 0);

  useEffect(() => {
    const count = billingMilestones?.length || 0;
    if (count > prevCount) {
      const lastIdx = count - 1;
      const el = rowRefs.current[lastIdx];
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
    setPrevCount(count);
  }, [billingMilestones?.length]);

  const renderAmount = (milestone) => {
    const value =
      milestone.milestone_amount ??
      calculateMilestoneAmount(parseFloat(milestone.percentage) || 0);
    return (
      <div style={amountCellStyle}>
        {renderCurrencyIcon("black-small")}
        <span>{formattedAmountWithoutAed(value) || "-"}</span>
      </div>
    );
  };

  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "none",
        margin: "1vh 0",
        border: "1px solid #E5E7EB",
        overflow: "initial",
      }}
    >
      <CardContent sx={{ padding: "16px" }}>
        <Typography
          variant="h6"
          sx={{
            marginBottom: "12px",
            letterSpacing: 0,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "16px",
          }}
        >
          <RiBillLine color="#4D6BF6" size={20} />
          Contractor Billing Milestones
        </Typography>

        <div
          style={{
            marginBottom: "16px",
            display: "flex",
            gap: "16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#F9FAFB",
              padding: "14px 16px",
              borderRadius: "10px",
              width: "100%",
              display: "flex",
              flexDirection: "row-reverse",
justifyContent: "space-between",
              gap: "4px",
              width: "100%",
            }}
          >
            <div style={amountCellStyle}>
              {renderCurrencyIcon("black-small")}
              <span style={{ fontSize: 18, fontWeight: 700 }}>
                {billingMilestonesData?.total_amount
                  ? formattedAmountWithoutAed(billingMilestonesData.total_amount)
                  : "-"}
              </span>
            </div>
            <Typography variant="body2" color="text.secondary">
              Your Quote Amount
            </Typography>
          </div>
        </div>

        <div style={{ padding: 0 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#0d0d0e",
              }}
            >
              Billing Milestones
            </Typography>

            {!isDisabled && (
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  style={{
                    width: "fit-content",
                    backgroundColor: "#4D6BF6",
                    borderRadius: "10px",
                    fontSize: "14px",
                    fontWeight: 600,
                    boxShadow: "none",
                    padding: "8px 16px",
                    border: "1px solid transparent",
                    color: "#fff",
                    cursor: "pointer",
                    fontFamily: "Inter Tight",
                  }}
                  onClick={addMilestone}
                >
                  + Add Milestone
                </button>
                {!hideSaveButton && (
                  <button
                    style={{
                      width: "fit-content",
                      backgroundColor: hasMilestoneChanges
                        ? "#10B981"
                        : "#9CA3AF",
                      borderRadius: "10px",
                      fontSize: "14px",
                      fontWeight: 600,
                      boxShadow: "none",
                      padding: "8px 16px",
                      border: "1px solid transparent",
                      color: "#fff",
                      cursor: hasMilestoneChanges ? "pointer" : "not-allowed",
                      fontFamily: "Inter Tight",
                    }}
                    onClick={saveMilestoneChanges}
                    disabled={!hasMilestoneChanges}
                  >
                    {isMilestoneLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                )}
              </div>
            )}
          </div>

          {billingMilestones?.map((milestone, index) => {
            const isExistingMilestone =
              milestone.id && typeof milestone.id === "string";
            const isEditable = !isDisabled && !isExistingMilestone;

            return (
              <Box
                ref={(el) => (rowRefs.current[index] = el)}
                key={index}
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "10px",
                  padding: "12px",
                  marginBottom: "10px",
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <Box sx={{ flex: 2, minWidth: 0 }}>
                  <CInput
                    label="Milestone Phase"
                    placeholder="e.g. Kickoff"
                    value={milestone.name || ""}
                    onChange={(e) =>
                      updateMilestone(index, "name", e.target.value)
                    }
                    required
                    disabled={!isEditable}
                    sx={inputColorSx}
                  />
                </Box>

                <Box sx={{ width: 140, flexShrink: 0 }}>
                  <CInput
                    label="Percentage (%)"
                    placeholder="%"
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    value={milestone.percentage || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^\d+(\.\d{0,2})?$/.test(v)) {
                        updateMilestone(index, "percentage", v);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "-" ||
                        e.key === "+" ||
                        e.key === "e" ||
                        e.key === "E" ||
                        e.key === "ArrowUp" ||
                        e.key === "ArrowDown"
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onWheel={(e) => e.target.blur()}
                    required
                    disabled={!isEditable}
                    sx={inputColorSx}
                  />
                </Box>

                <Box
                  sx={{
                    flex: 1.2,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  {renderAmount(milestone)}
                </Box>

                {!isDisabled && (
                  <IconButton
                    color="error"
                    onClick={() => deleteMilestone(index)}
                    sx={{ marginLeft: "4px", flexShrink: 0 }}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            );
          })}

          {/* Billing Schedule Summary */}
          {/* <Box sx={{ marginTop: "20px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                gap: "10px",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#0d0d0e",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <MdOutlineCalendarMonth color="#fa832e" size={16} />
                Billing Schedule Summary
              </Typography>
            </div>

            {billingMilestones?.map((milestone, index) => (
              <Box
                key={`summary-${index}`}
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  padding: "12px 14px",
                  marginBottom: "10px",
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    backgroundColor: "#fa832e1a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#fa832e",
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: "#0d0d0e" }}>
                    {milestone.name || "New Milestone"}
                  </div>
                </Box>

                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                  {renderAmount(milestone)}
                  <Typography
                    variant="body2"
                    sx={{ color: "#6B7280", fontSize: 13, marginTop: "2px" }}
                  >
                    {milestone.percentage || 0}%
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box> */}
        </div>
      </CardContent>
    </Card>
  );
};

export default BillingMilestones;
